import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { MyAELayout } from "../components/myae/Layout";
import { MyAEProgress } from "../components/myae/MyAEProgress";
import { UploadPanel } from "../components/examiner/UploadPanel";
import { startAnalysis, getAnalysis } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { track } from "../lib/analytics";
import { toast } from "sonner";

export default function NewAnalysis() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuthOpen } = useAuth();
  const [view, setView] = useState("input"); // input | processing
  const [done, setDone] = useState(false);
  const pollRef = useRef(null);
  const startedRef = useRef(false);

  const poll = (id) => {
    clearInterval(pollRef.current);
    const deadline = Date.now() + 3 * 60 * 1000; // stop polling after 3 minutes
    pollRef.current = setInterval(async () => {
      if (Date.now() > deadline) {
        clearInterval(pollRef.current);
        setView("input");
        toast.error("This is taking longer than expected. Please try again.");
        return;
      }
      try {
        const doc = await getAnalysis(id);
        if (doc.status === "complete") {
          clearInterval(pollRef.current);
          setDone(true);
          track("analysis_completed", { grade: doc.report?.estimated_grade?.percentage });
          setTimeout(() => navigate(`/report/${id}`), 900);
        } else if (doc.status === "error") {
          clearInterval(pollRef.current);
          setView("input");
          toast.error("Analysis failed: " + (doc.error || "unknown error"));
        }
      } catch (e) { /* keep polling */ }
    }, 2500);
  };

  const handleAnalyze = async (payload) => {
    setView("processing"); setDone(false);
    const isRevision = !!payload.parent_id;
    track(isRevision ? "revision_started" : "analysis_started", { has_brief: !!payload.question });
    try {
      const { id } = await startAnalysis(payload);
      poll(id);
    } catch (e) {
      setView("input");
      // 429 = fair-use hit while promo is on. Clear, friendly, NEVER push to upgrade.
      if (e?.response?.status === 429 && e.response.data?.detail?.reason === "fair_use_hit") {
        const d = e.response.data.detail;
        toast.message(d.message || "You've reached the temporary fair-use limit. Please try again later.", {
          description: d.try_after ? `Try again after ${new Date(d.try_after).toLocaleString()}` : undefined,
          duration: 8000,
        });
        return;
      }
      if (e?.response?.status === 402) {
        const reason = e.response.data?.detail;
        if (reason === "sign_in_required") { toast.message("Please sign in to run another report."); setAuthOpen(true); }
        else if (reason === "pro_required_for_revision") { toast.message("Revised drafts are a Pro feature."); navigate("/pricing"); }
        else { toast.message("You've used your free reports. Upgrade to continue."); navigate("/pricing"); }
        return;
      }
      toast.error(e?.response?.data?.detail || "Could not start analysis.");
    }
  };

  // Auto-start when duplicated from the dashboard
  useEffect(() => {
    if (location.state?.autostart && !startedRef.current) {
      startedRef.current = true;
      handleAnalyze(location.state.autostart);
      window.history.replaceState({}, "");
    }
    return () => clearInterval(pollRef.current);
  }, []); // eslint-disable-line

  return (
    <MyAELayout>
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-8 sm:py-10" data-testid="new-analysis-page">
        {view === "input" && (
          <>
            <div className="mb-6">
              <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-900">New analysis</h1>
              <p className="text-zinc-500 mt-1">Upload your assignment brief, supporting documents and essay.</p>
            </div>
            <div className="rounded-2xl border border-zinc-200 bg-white shadow-sm overflow-hidden">
              <UploadPanel onAnalyze={handleAnalyze} />
            </div>
          </>
        )}
        {view === "processing" && <MyAEProgress done={done} />}
      </div>
    </MyAELayout>
  );
}
